import { cn, DynamicMyoshLogo } from '@myosh/odin-components';
import { memo, useCallback, useRef } from 'react';
import { useDiagramContext } from '../../context/diagram.context';
import useHeaderActions from '../../hooks/use-header-actions';
import { useAppSelector } from '../../redux/hooks';
import { selectDiagramDisabled, selectMueRecordId } from '../../redux/slices/diagram';
import { JobStatus, selectHasRunningJobs, selectSubmittedJobs } from '../../redux/slices/running-jobs';
import AiAnalysisSuggestionContainer, {
  type AIAnalysisSuggestionContainerRef,
} from '../common/ai-analysis-suggestion-container';
import AISplitButton from './ai-split-button.component';
import ControlLegend from './control-legend.component';
import CreateSplitButton from './create-split-button.component';
import FilterOverlay from './filter/filter-overlay.component';
import RecordDropDown from './record-drop-down.component';
import RunningJobsPanel from './running-jobs-panel.component';
import Settings from './settings.component';

function Header() {
  const { isAIGenerated, isSavedDiagram } = useDiagramContext();
  const { bowtieDtoCreate, bowtieDtoSave, bowtieDtoAnalyze, mueLabel } = useHeaderActions();

  const hasRunningJobs = useAppSelector(selectHasRunningJobs);
  const submittedJobs = useAppSelector(selectSubmittedJobs);
  const latestJobStatus = submittedJobs[0]?.status;

  const mueRecordId = useAppSelector(selectMueRecordId);
  const canSaveDiagram = !mueRecordId;

  const disableDiagram = useAppSelector(selectDiagramDisabled);

  const analysisRef = useRef<AIAnalysisSuggestionContainerRef>(null);

  const onAIAnalysisReady = useCallback((text: string) => {
    analysisRef.current?.show(text);
  }, []);

  return (
    <div data-cy="header-root" className="relative flex w-full flex-col">
      {disableDiagram && <div className="absolute z-1301 h-full w-full bg-transparent" />}
      <div
        className="sticky top-0 left-0 z-1300 mt-0 flex h-14 justify-between bg-background px-2 pt-2"
        id="header"
        data-cy="header-bar"
      >
        <div className="flex flex-row gap-3">
          <DynamicMyoshLogo />
          {!isAIGenerated && (
            <>
              <RecordDropDown />
              <AISplitButton onAIAnalysisReady={onAIAnalysisReady} bowtieDtoAnalyze={bowtieDtoAnalyze} />
            </>
          )}
          {(isAIGenerated || isSavedDiagram || canSaveDiagram) && (
            <div
              className={cn('flex flex-row gap-2', {
                hidden:
                  hasRunningJobs || latestJobStatus === JobStatus.Success || latestJobStatus === JobStatus.Warning,
              })}
              data-cy="create-split-container"
            >
              <CreateSplitButton
                bowtieDtoCreate={bowtieDtoCreate}
                bowtieDtoSave={bowtieDtoSave}
                mueLabel={mueLabel}
                mueRecordId={mueRecordId}
              />
            </div>
          )}
          <RunningJobsPanel submittedJobs={submittedJobs} />
          <FilterOverlay />
        </div>
        <div className="flex min-w-[435px]">
          <ControlLegend />
          <Settings />
        </div>
      </div>
      <AiAnalysisSuggestionContainer ref={analysisRef} />
    </div>
  );
}

export default memo(Header);
